// Pareamento por QR.
//
// O QR da Evolution expira em poucos segundos e é reemitido: por isso a tela
// pede um novo a cada ~20s enquanto ninguém escaneia. Junto disso roda o
// polling do estado, que é o que faz a tela virar sozinha para "conectado"
// quando o celular lê o código — sem reload manual, que é o critério de aceite
// da fase.
//
// Os dois relógios são separados de propósito: o do QR é caro (a Evolution
// reabre a sessão) e o do estado é barato.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, RefreshCw, Smartphone, X } from 'lucide-react';
import type { ConexaoPublica } from '../tipos';
import { consultarEstado, obterQr } from '../apiConexoes';

const INTERVALO_QR_MS = 20000;
const INTERVALO_ESTADO_MS = 3000;

interface Props {
  conexao: ConexaoPublica;
  onFechar: () => void;
  onConectou: () => void;
}

export default function ModalPareamento({ conexao, onFechar, onConectou }: Props) {
  const [qr, setQr] = useState<string | null>(null);
  const [pairing, setPairing] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [conectou, setConectou] = useState(false);
  const paradoRef = useRef(false);

  const buscarQr = useCallback(async () => {
    if (paradoRef.current) return;
    setCarregando(true);
    setErro(null);
    try {
      const r = await obterQr(conexao.id);
      if (paradoRef.current) return;
      if (r.ja_conectada) {
        setConectou(true);
        paradoRef.current = true;
        onConectou();
        return;
      }
      setQr(r.qr_base64 ?? null);
      setPairing(r.pairing_code ?? null);
      if (!r.qr_base64 && !r.qr_code) {
        setErro('A Evolution respondeu sem QR. Tente reiniciar a conexão.');
      }
    } catch (e: any) {
      if (!paradoRef.current) setErro(e.message ?? 'Falha ao pedir o QR.');
    } finally {
      if (!paradoRef.current) setCarregando(false);
    }
  }, [conexao.id, onConectou]);

  useEffect(() => {
    paradoRef.current = false;
    void buscarQr();
    const tQr = setInterval(() => { void buscarQr(); }, INTERVALO_QR_MS);
    const tEstado = setInterval(async () => {
      if (paradoRef.current) return;
      try {
        const r = await consultarEstado(conexao.id);
        if (r.status === 'active' && !paradoRef.current) {
          paradoRef.current = true;
          setConectou(true);
          onConectou();
        }
      } catch { /* silencioso: é polling, e o erro real aparece no banner */ }
    }, INTERVALO_ESTADO_MS);

    return () => {
      paradoRef.current = true;
      clearInterval(tQr);
      clearInterval(tEstado);
    };
  }, [conexao.id, buscarQr, onConectou]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5 dark:border-slate-700">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Parear {conexao.display_name}</h2>
            <p className="truncate text-xs text-gray-500 dark:text-slate-400">
              instância {conexao.external_id}
            </p>
          </div>
          <button
            onClick={onFechar}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </header>

        <div className="px-5 py-5">
          {conectou ? (
            <div className="flex flex-col items-center py-6 text-center">
              <CheckCircle2 size={44} className="text-emerald-500" />
              <p className="mt-3 text-sm font-bold text-gray-900 dark:text-white">Conectado</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                O número está pareado. A ingestão continua desligada até você ligar — é o último passo.
              </p>
              <button
                onClick={onFechar}
                className="mt-4 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
              >
                Fechar
              </button>
            </div>
          ) : (
            <>
              <ol className="mb-4 space-y-1 text-xs text-gray-600 dark:text-slate-300">
                <li>1. Abra o WhatsApp no celular do número comercial.</li>
                <li>2. Toque em <span className="font-semibold">Aparelhos conectados → Conectar aparelho</span>.</li>
                <li>3. Aponte a câmera para o código abaixo.</li>
              </ol>

              <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
                {carregando && !qr ? (
                  <Loader2 size={26} className="animate-spin text-gray-300 dark:text-slate-600" />
                ) : qr ? (
                  // Fundo branco fixo: QR escuro em tema escuro não é lido pela
                  // câmera, e o erro pareceria da Evolution.
                  <div className="rounded-xl bg-white p-2.5">
                    <img src={qr} alt="QR code para parear o WhatsApp" className="h-52 w-52" />
                  </div>
                ) : (
                  <p className="px-4 text-center text-xs text-gray-500 dark:text-slate-400">
                    {erro ?? 'Sem QR no momento.'}
                  </p>
                )}
              </div>

              {pairing && (
                <p className="mt-3 text-center text-xs text-gray-600 dark:text-slate-300">
                  Sem câmera? Use o código de pareamento:{' '}
                  <span className="font-mono text-sm font-bold tracking-widest text-gray-900 dark:text-white">
                    {pairing}
                  </span>
                </p>
              )}

              {erro && qr && (
                <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  {erro}
                </p>
              )}

              <div className="mt-4 flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-slate-500">
                  <Smartphone size={12} />
                  A tela vira sozinha quando o celular ler.
                </p>
                <button
                  onClick={() => { void buscarQr(); }}
                  disabled={carregando}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                >
                  <RefreshCw size={12} className={carregando ? 'animate-spin' : ''} />
                  Novo código
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
